import {useState} from 'react'

import type {AuthUser} from '../auth/types'
import {useCurrentUser} from '../auth/use-current-user'

export type MineOrAll = 'mine' | 'all'

function buildMineOrAllStorageKey(list: string, userId: string) {
    return `mattech:${list}-scope:${userId}`
}

function readStoredMineOrAllScope(key: string | null, fallback: MineOrAll): MineOrAll {
    if (!key) {
        return fallback
    }

    try {
        const stored = localStorage.getItem(key)
        return stored === 'all' || stored === 'mine' ? stored : fallback
    } catch {
        return fallback
    }
}

export function useMineOrAll(
    list: 'jobs' | 'projects' | 'active-rentals',
    defaultFor?: (user: AuthUser) => MineOrAll,
): [MineOrAll, (scope: MineOrAll) => void] {
    const user = useCurrentUser()
    const userId = user?.id ?? null
    const fallback: MineOrAll = user && defaultFor ? defaultFor(user) : 'mine'
    const key = userId ? buildMineOrAllStorageKey(list, userId) : null
    const [state, setState] = useState<{key: string | null; scope: MineOrAll}>(() => ({
        key,
        scope: readStoredMineOrAllScope(key, fallback),
    }))
    const scope = state.key === key ? state.scope : readStoredMineOrAllScope(key, fallback)

    function setScope(next: MineOrAll) {
        setState({key, scope: next})
        if (key) {
            try {
                localStorage.setItem(key, next)
            } catch {
                // Ignore storage errors.
            }
        }
    }

    return [scope, setScope]
}
